import { Link } from "react-router-dom";
import type { MouseEvent } from "react";
import type { Movie } from "@/mocks/movies";
import useFavorites from "@/hooks/useFavorites";

interface MovieCardProps {
  movie: Movie;
  showSynopsis?: boolean;
}

export default function MovieCard({
  movie,
  showSynopsis = true,
}: MovieCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const fav = isFavorite(movie.id);

  const onFav = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(movie.id);
  };

  return (
    <article className="group relative">
      <div className="relative w-full overflow-hidden rounded-lg bg-secondary-100">
        <Link to={`/detail/${movie.id}`} className="block">
          <div className="relative aspect-[2/3] w-full">
            <img
              src={movie.cover}
              alt={`${movie.title} 封面`}
              title={`${movie.title} 百合影视`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            <span className="absolute bottom-2 left-2 rounded-md bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
              <i className="ri-star-fill mr-1 text-accent-300" />
              {movie.rating.toFixed(1)}
            </span>
            <span className="absolute left-2 top-2 rounded-md bg-primary-500/90 px-2 py-0.5 text-[11px] font-medium text-background-50">
              {movie.type}
            </span>
          </div>
        </Link>
        <button
          type="button"
          onClick={onFav}
          aria-label={fav ? "取消收藏" : "收藏"}
          className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-sm backdrop-blur-sm transition-colors ${
            fav
              ? "bg-accent-500 text-background-50"
              : "bg-black/35 text-white hover:bg-black/55"
          }`}
        >
          <i className={fav ? "ri-heart-3-fill" : "ri-heart-3-line"} />
        </button>
      </div>

      <div className="mt-3">
        <Link to={`/detail/${movie.id}`}>
          <h3 className="text-sm font-semibold text-foreground-950 transition-colors line-clamp-1 group-hover:text-primary-600 md:text-base">
            {movie.title}
          </h3>
        </Link>
        <p className="mt-1 text-xs text-foreground-500">
          {movie.year} · {movie.region}
        </p>
        {showSynopsis && (
          <p className="mt-2 text-xs leading-relaxed text-foreground-600 line-clamp-2">
            {movie.synopsis}
          </p>
        )}
      </div>
    </article>
  );
}